import {
  deriveRecipientViewStats,
  hasSilentModeEvent,
  latestAccessChange,
  mapJourneyEvents,
} from './mapJourneyEvents'

export const isElectiveTriggeredPolicies = (policies = []) =>
  policies.some((policy) => String(policy).toLowerCase() === 'elective')

const API_STATUS_TO_UI = {
  Opened: 'read',
  'In-flight': 'processing',
  Expired: 'expired',
}

export const getEncryptionMethodFromTriggeredPolicies = (policies = []) => {
  if (isElectiveTriggeredPolicies(policies)) return 'Elective'
  if (policies.length > 0) return 'DLP'
  return null
}

const formatTriggerLabel = (policy) => {
  if (String(policy).toLowerCase() === 'elective') return 'Elective'
  return String(policy)
}

const deriveTriggers = (row) => {
  const policies = row.triggeredPolicies || row.triggered_policies || []
  return policies.map(formatTriggerLabel).filter(Boolean)
}

const deriveEncryptionType = (row) => {
  const policies = row.triggeredPolicies || row.triggered_policies || []
  // Mail encrypted through the x-oes-secure header has no policies; the Elective card counts it.
  if (
    row.action === 'encrypt' &&
    (policies.length === 0 || isElectiveTriggeredPolicies(policies))
  ) {
    return 'Elective'
  }
  return getEncryptionMethodFromTriggeredPolicies(policies) || 'DLP'
}

const normalizeRecipient = (recipient) => {
  if (typeof recipient === 'string') {
    return { email: recipient, emailId: null, status: 'processing' }
  }
  return {
    email: recipient?.email || recipient?.address || '',
    emailId: recipient?.emailId || recipient?.email_id || null,
    status: API_STATUS_TO_UI[recipient?.status] || recipient?.status || 'processing',
  }
}

const deriveRowStatus = (row, recipients, silentMode = false) => {
  const withSilentMode = (status) => (silentMode && status === 'processing' ? 'sent' : status)

  const statuses = recipients.map((r) => r.status)
  if (statuses.includes('revoked')) return 'revoked'

  const explicit = API_STATUS_TO_UI[row.status] || row.status
  if (explicit) return withSilentMode(explicit)
  if (statuses.includes('read') || statuses.includes('accessed') || statuses.includes('replied')) {
    return 'read'
  }
  if (statuses.includes('expired')) return 'expired'
  if (statuses.includes('sent')) return 'sent'
  return withSilentMode('processing')
}

const deriveReceivedAt = (row, events = []) => {
  if (row.receivedAt || row.received_at) return row.receivedAt || row.received_at
  const received = events.find((e) => (e.eventType || e.event_type) === 'email.received')
  return received?.data?.receivedAt || received?.eventTimestamp || received?.event_timestamp || null
}

export const mapTrackedEmailRow = (row = {}) => {
  const recipients = (row.recipients || []).map(normalizeRecipient)
  const triggers = deriveTriggers(row)
  const rawEvents = row.events || []
  const expiresAt = row.expiresAt || row.expires_at || null
  const linkExpirationEpoch = expiresAt
    ? new Date(expiresAt).getTime()
    : row.linkExpirationEpoch || row.link_expiration_epoch || null
  const events = mapJourneyEvents(rawEvents, {
    sender: row.sender || row.from,
    triggers,
    recipientsByEmailId: Object.fromEntries(
      recipients.filter((r) => r.emailId).map((r) => [r.emailId, r.email])
    ),
  })

  const rowSilentMode = row.silentMode === true
  const silentMode = rowSilentMode || hasSilentModeEvent(rawEvents)
  const accessChange = latestAccessChange(rawEvents)
  const accessAction = accessChange?.action ?? null

  const recipientsWithViews = recipients.map((recipient) => {
    const withViews = {
      ...recipient,
      ...deriveRecipientViewStats(rawEvents, recipient.emailId),
    }
    const recipientSilentMode = rowSilentMode || hasSilentModeEvent(rawEvents, recipient.emailId)
    if (withViews.status === 'processing' && recipientSilentMode) {
      return { ...withViews, status: 'sent' }
    }
    return withViews
  })

  let status = deriveRowStatus(row, recipientsWithViews, silentMode)
  let revoked = status === 'revoked'
  if (accessAction === 'revoked') {
    status = 'revoked'
    revoked = true
  } else if (accessAction === 'restored') {
    revoked = false
    if (status === 'revoked') status = 'sent'
  }

  return {
    id: row.messageIdNormalized || row.message_id_normalized || row.id,
    messageId: row.messageId || row.message_id || null,
    receivedAt: deriveReceivedAt(row, rawEvents),
    from: row.sender || row.from,
    subject: row.subject,
    encryptionType: deriveEncryptionType(row),
    triggers,
    trigger: triggers[0] || null,
    expiresAt,
    linkExpiration: expiresAt,
    linkExpirationEpoch: Number.isFinite(linkExpirationEpoch) ? linkExpirationEpoch : null,
    silentMode,
    accessAction,
    accessActionAt: accessChange?.at ?? null,
    revoked,
    status,
    recipients: recipientsWithViews.length
      ? recipientsWithViews
      : [
          {
            email: '—',
            emailId: null,
            status: 'processing',
            viewCount: 0,
            openedAt: null,
            userAgent: null,
          },
        ],
    events,
  }
}

export const mapTrackedEmailsResponse = (response) => {
  const payload = response?.data ?? response
  const items = payload?.items || payload?.results || payload?.emails || []
  return {
    items: items.map(mapTrackedEmailRow),
    nextCursor: payload?.nextCursor ?? payload?.next_cursor ?? null,
    total: payload?.total ?? items.length,
  }
}

export const mapTrackedEmailsSummaryResponse = (response) => {
  const payload = response?.data ?? response ?? {}
  return {
    summary: {
      scannedEmails: payload.scannedEmails ?? null,
      encryptedEmails: payload.encryptedEmails ?? null,
      dlp: payload.dlp ?? null,
      elective: payload.elective ?? null,
      revoked: payload.revoked ?? null,
      uniqueSenders: payload.uniqueSenders ?? null,
      dlpChange: payload.dlpChange ?? null,
      electiveChange: payload.electiveChange ?? null,
      revokedChange: payload.revokedChange ?? null,
    },
    windowFrom: payload.windowFrom ?? null,
    windowTo: payload.windowTo ?? null,
    plan: payload.plan ?? null,
  }
}

// A change with no company keeps the current selection (PartnerOptions reducer), so it is not a switch.
export const isOtherCompany = (companyFilterChange, companyId) => {
  const next = companyFilterChange?.payload?.selectedCompany?.value
  return next != null && String(next) !== String(companyId ?? '')
}
